import * as Clipboard from 'expo-clipboard';
import { Link, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import type { ComponentProps } from 'react';
import { useFormContext } from 'react-hook-form';
import { Share as RNShare, ScrollView, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useQuery } from 'urql';
import FadeInContent from '@/components/Animation/FadeInContent';
import Avatar from '@/components/Custom/Avatar';
import * as Header from '@/components/Custom/Header';
import * as Placeholder from '@/components/Custom/Placeholder';
import type { EventEditFormValues } from '@/components/Event/EventEdit.types';
import Box from '@/components/UI/Box';
import Button from '@/components/UI/Button';
import Card from '@/components/UI/Card';
import Item from '@/components/UI/PressableRow';
import Text from '@/components/UI/Text';
import Icon from '@/components/Unistyles/Icon';
import { graphql } from '@/gql/graphql';
import useIsSharingAvailable from '@/hooks/useIsSharingAvailable';
import useTeamInviteCode from '@/hooks/useTeamInviteCode';
import useTimer from '@/hooks/useTimer';
import { gotoOrganizationDetail } from '@/stores/useRootStore';
import { EVENT_TEAM_LIMIT } from '@/utils/constants';
import getEmptyEntity from '@/utils/getEmptyEntity';
import getRandomColor from '@/utils/getRandomColor';
import getTeamInviteLink from '@/utils/getTeamInviteLink';
import hapticsOnPress from '@/utils/hapticsOnPress';

const GetEventCreateDone = graphql(/* GraphQL */ `
  query GetEventCreateDone($eventId: uuid!, $teamLimit: Int!) {
    event_by_pk(id: $eventId) {
      id
      title
      permissions
      parent: event {
        id
        title
        parentId: parent_id
      }
      event_teams(
        limit: $teamLimit
        order_by: [{ is_home: asc }, { team: { title: asc } }]
      ) {
        event_id
        team_id
        team {
          id
          title
          avatar_id
          home_primary_color
        }
      }
    }
  }
`);

type ShareTextState = {
  text: string;
  variant: ComponentProps<typeof Text>['variant'];
};

export default function EventCreateDone() {
  const {
    reset,
    formState: { defaultValues },
  } = useFormContext<EventEditFormValues>();
  const { navigate, replace, dismissTo } = useRouter();
  const { getInviteCode } = useTeamInviteCode();
  const isSharingAvailable = useIsSharingAvailable();

  const { eventId } = useLocalSearchParams<{
    eventId?: string;
  }>();

  const [{ data, fetching }] = useQuery({
    query: GetEventCreateDone,
    variables: {
      eventId: eventId || '',
      teamLimit: EVENT_TEAM_LIMIT,
    },
  });

  const [shareText, setShareText] = useTimer<ShareTextState>(
    {
      text: 'Share Team Invite Links',
      variant: 'typography',
    },
    2000,
  );

  const event = data?.event_by_pk;
  const isOrganization = !event?.parent;
  const isLeague = event?.parent && !event.parent.parentId;
  const isSeason = !!event?.parent?.parentId;

  const onPressShare = async (id: string, title: string) => {
    const inviteCode = await getInviteCode(id);

    if (inviteCode) {
      const inviteLink = getTeamInviteLink(inviteCode);

      if (isSharingAvailable) {
        await RNShare.share({
          message: `Join ${title} on 1Time Sports: ${inviteLink}`,
        });
        return;
      }

      await Clipboard.setStringAsync(inviteLink);

      setShareText({
        text: `Copied ${title} Link to Clipboard`,
        variant: 'primary',
      });
    }
  };

  if (!event?.permissions?.edit && !fetching) {
    return (
      <Placeholder.Container>
        <Placeholder.Icon name="close-outline" />
        <Placeholder.Text>
          You don't have permission to edit this event.
        </Placeholder.Text>
      </Placeholder.Container>
    );
  }

  return (
    <FadeInContent fetching={fetching}>
      <ScrollView contentContainerStyle={style.scrollContent}>
        <Stack.Screen
          options={{
            title: event?.title || '',
          }}
        />
        <Box direction="vertical" space="sm" mx="md">
          <Card p="lg">
            <Box direction="vertical" space="md">
              <Box direction="vertical" align="center" space="xs">
                <Text size="2xl" center>
                  🎉 Great! You just created
                </Text>
                <Text size="2xl" variant="primary" center>
                  {event?.title}
                </Text>
                <Text center>What would you like to do next?</Text>
              </Box>

              <Box direction="horizontal" justify="center" space="sm" wrap>
                {isOrganization ? (
                  <Button
                    onPress={() => {
                      setTimeout(() => {
                        reset({
                          ...defaultValues,
                          parentId: eventId,
                          teams: Array.from({ length: 4 }).map(() =>
                            getEmptyEntity(getRandomColor()),
                          ),
                          eventType: 'group',
                        });

                        replace({
                          pathname: '/event/create',
                        });
                      }, 0);
                    }}
                    size="sm"
                    appearance="outline"
                    variant="positive"
                    testID="EventCreateDoneLeagueUnderButton"
                  >
                    <Button.Text numberOfLines={1}>
                      Create League Under {data?.event_by_pk?.title}
                    </Button.Text>
                  </Button>
                ) : null}
                {isLeague ? (
                  <>
                    <Button
                      onPress={() => {
                        setTimeout(() => {
                          reset({
                            ...defaultValues,
                            parentId: event?.parent?.id,
                            eventType: 'group',
                          });

                          replace({
                            pathname: '/event/create',
                          });
                        }, 0);
                      }}
                      size="sm"
                      appearance="outline"
                      variant="positive"
                      maxWidth={80}
                      testID="EventCreateDoneLeagueButton"
                    >
                      <Button.Text numberOfLines={1}>
                        Create Another League
                      </Button.Text>
                    </Button>
                    <Button
                      onPress={() => {
                        setTimeout(() => {
                          reset({
                            ...defaultValues,
                            parentId: eventId,
                            eventType: 'group',
                          });

                          replace({
                            pathname: '/event/create',
                          });
                        }, 0);
                      }}
                      size="sm"
                      appearance="outline"
                      variant="positive"
                      testID="EventCreateDoneSeasonUnderButton"
                    >
                      <Button.Text numberOfLines={1}>
                        Create Season Under {data?.event_by_pk?.title}
                      </Button.Text>
                    </Button>
                  </>
                ) : null}
                {isSeason ? (
                  <>
                    <Button
                      onPress={() => {
                        dismissTo({
                          pathname: '/event/[eventId]/generate',
                          params: { eventId },
                        });
                      }}
                      size="sm"
                      appearance="outline"
                      variant="positive"
                      testID="EventCreateDoneGenerateGamesButton"
                    >
                      <Button.Text numberOfLines={1}>
                        Generate Games
                      </Button.Text>
                    </Button>

                    <Button
                      onPress={() => {
                        setTimeout(() => {
                          reset({
                            ...defaultValues,
                            parentId: event?.parent?.id,
                            eventType: 'group',
                          });

                          replace({
                            pathname: '/event/create',
                          });
                        }, 0);
                      }}
                      size="sm"
                      appearance="outline"
                      variant="positive"
                      testID="EventCreateDoneSeasonButton"
                    >
                      <Button.Text numberOfLines={1}>
                        Create Another Season
                      </Button.Text>
                    </Button>
                  </>
                ) : null}
                <Link
                  dismissTo
                  href={{
                    pathname: isOrganization
                      ? '/organization'
                      : '/organization/schedule/[eventId]',
                    params: { eventId: data?.event_by_pk?.id },
                  }}
                  asChild
                >
                  <Button
                    onPress={() => {
                      if (isOrganization && data?.event_by_pk?.id) {
                        gotoOrganizationDetail(data.event_by_pk.id);
                      } else {
                        navigate({
                          pathname: '/organization/schedule/[eventId]',
                          params: { eventId: event?.id },
                        });
                      }
                    }}
                    size="sm"
                    appearance="outline"
                    variant="primary"
                  >
                    <Button.Text>Go To {event?.title}</Button.Text>
                  </Button>
                </Link>
              </Box>
            </Box>
          </Card>
          {event?.event_teams.length ? (
            <Card>
              <Box direction="vertical" space="md">
                <Text
                  bold
                  size="xl"
                  px="xl"
                  pt="lg"
                  variant={shareText.variant}
                >
                  {shareText.text}
                </Text>
                <View>
                  {event?.event_teams.map((eventTeam) => (
                    <Item
                      onPress={hapticsOnPress(() => {
                        onPressShare(eventTeam.team.id, eventTeam.team.title);
                      })}
                      key={eventTeam.team_id}
                      spaced
                    >
                      <Box direction="horizontal" space="md" align="center">
                        <Avatar
                          id={eventTeam.team?.avatar_id ?? ''}
                          name={eventTeam.team?.title}
                          color={eventTeam.team?.home_primary_color}
                        />
                        <Text>{eventTeam.team?.title}</Text>
                      </Box>
                      <Header.Button
                        onPress={hapticsOnPress(() => {
                          onPressShare(eventTeam.team.id, eventTeam.team.title);
                        })}
                        testID="EventCreateDoneTeamShareButton"
                      >
                        <Icon name="share-outline" />
                      </Header.Button>
                    </Item>
                  ))}
                </View>
              </Box>
            </Card>
          ) : null}
        </Box>
      </ScrollView>
    </FadeInContent>
  );
}

const style = StyleSheet.create((theme) => ({
  scrollContent: {
    paddingVertical: theme.space('xl'),
  },
}));
